import { Loader2, Clock, Gauge } from "lucide-react";
import { fmtClock } from "../hooks/useBuildClock";

const COPY = {
  cap: { badge: "Limit reached", title: "Your duck is saved — waiting for a top-up" },
  ai: { badge: "Waiting for AI service", title: "Your duck is waiting for the AI service" },
  gate: { badge: "Queued", title: "Your duck is in the queue" },
  waiting: { badge: "Waiting for sculptor", title: "Sculptors are busy — we'll keep trying" },
  views: { badge: "Rendering side views", title: "Drawing the back and sides of your duck" },
  building: { badge: "Building 3D model", title: "Turning your render into a real Duck" },
};

// Badge, headline and explanation for the active build states (plus the two "parked, not failed" ones).
export const BuildHeadline = ({ creation, waiting, viewsStage, retryIn }) => {
  const capped = waiting && creation.cap_blocked;
  const aiLimited = waiting && !capped && creation.ai_limit_blocked;
  const gated = waiting && !capped && !aiLimited && creation.gate_blocked;
  const copy = COPY[capped ? "cap" : aiLimited ? "ai" : gated ? "gate" : waiting ? "waiting" : viewsStage ? "views" : "building"];
  return (
    <>
      <span className={`inline-flex items-center gap-2 rounded-full text-xs font-mono2 uppercase tracking-widest px-3 py-1 ${capped ? "bg-rose-100 text-rose-700" : "bg-accent/15 text-accent"}`}>
        {capped ? <Gauge className="h-3.5 w-3.5" /> : waiting ? <Clock className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {copy.badge}
      </span>
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{copy.title}</h2>
      {capped ? (
        <p data-testid="build-cap-waiting" className="text-muted-foreground">
          {creation.build_error} Everything you made so far is kept exactly as it is, and this duck carries on from
          here by itself the moment your allowance is topped up.
        </p>
      ) : aiLimited ? (
        <p data-testid="build-ai-limit-waiting" className="text-muted-foreground">
          {creation.build_error} Everything you made so far is kept, nothing is charged while it waits, and you can close
          this tab — leave an email below if you'd like us to ping you when it's ready.
        </p>
      ) : gated ? (
        <p data-testid="build-gate-waiting" className="text-muted-foreground">
          {creation.build_error} We check again every couple of minutes and your duck carries on from exactly here —
          you can close this tab, and leave an email below if you'd like us to ping you when it's ready.
        </p>
      ) : waiting ? (
        <p data-testid="build-waiting" className="text-muted-foreground">
          {creation.build_error} Next attempt in <span className="font-mono2 font-semibold">{fmtClock(retryIn)}</span>
          {creation.build_attempts ? ` (attempt ${creation.build_attempts} done)` : ""}. We retry every 3 minutes for up to 2 hours
          {creation.max_sculpt_submissions ? ` (at most ${creation.max_sculpt_submissions} paid sculpts)` : ""} —
          no need to keep this tab open if you leave an email below.
        </p>
      ) : (
        <p className="text-muted-foreground">
          The AI Duck Sculptor converts the approved render into a highly-detailed, textured 3D mesh. This usually takes 5-10 minutes
          depending on complexity and may take longer. You can keep this tab open or come back to it later by from the gallery.
        </p>
      )}
    </>
  );
};
